import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { ActivityIndicator, FlatList, RefreshControl, View } from 'react-native'
import { StackScreenProps } from '@react-navigation/stack'
import type { AdminNavigatorParams } from '../../navigation/types'
import { useTranslation } from 'react-i18next'
import { ScreenWrapper } from '../../@ui/containers/grid/screen.wrapper'
import Row from '../../@ui/containers/grid/row'
import Column from '../../@ui/containers/grid/column'
import { MelddSearchBar } from '../../@ui/form/meldd-search.input'
import { Dropdown } from '../../@ui'
import { SortOptions } from '../../@ui/buttons'
import type { SortDirection } from '../../@ui/buttons'
import { Text, IconButton } from 'react-native-paper'
import {
  CouponSortField,
  OrderDirection,
  type CouponFilterInput,
  useCouponsQuery,
  useDeleteCouponMutation,
  useUpdateCouponMutation,
} from '../../generated/graphql'
import { useFocusEffect } from '@react-navigation/native'
import { CouponCard } from './components/coupon.card'
import { CouponRedemptionsModal } from './components/coupon-redemptions.modal'
import { EditCouponModal } from './components/edit-coupon.modal'
import type { AdminCouponRow } from './components/coupon.card'
import Toast from 'react-native-root-toast'
import { MessageType } from '../../utils/message-type'
import { getFirstGraphQlErrorMessage } from '../../utils/error-utils'
import * as Sentry from '@sentry/react-native'

type ListFilterUi = 'all' | 'active' | 'expired' | 'disabled'

const FILTER_LABEL_KEYS: Record<ListFilterUi, string> = {
  all: 'filter-all',
  active: 'filter-active',
  expired: 'filter-expired',
  disabled: 'filter-disabled',
}

const FILTER_DROPDOWN_ORDER: ListFilterUi[] = [
  'all',
  'active',
  'expired',
  'disabled',
]

export function CouponManagementScreen({
  navigation,
}: StackScreenProps<AdminNavigatorParams, 'CouponManagement'>) {
  const { t } = useTranslation('admin')
  const [searchQuery, setSearchQuery] = useState('')
  const [listFilterUi, setListFilterUi] = useState<ListFilterUi>('active')
  const [sortField, setSortField] = useState<CouponSortField>(
    CouponSortField.CreatedAt
  )
  const [sortDirection, setSortDirection] = useState<SortDirection>('DESC')
  const [couponModalVisible, setCouponModalVisible] = useState(false)
  const [modalCoupon, setModalCoupon] = useState<AdminCouponRow | null>(null)
  const [redemptionsVisible, setRedemptionsVisible] = useState(false)
  const [redemptionsCoupon, setRedemptionsCoupon] =
    useState<AdminCouponRow | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const filterArg = useMemo((): CouponFilterInput | undefined => {
    if (listFilterUi === 'all') return undefined
    if (listFilterUi === 'disabled') return { enabled: false }
    if (listFilterUi === 'active') return { enabled: true, isExpired: false }
    if (listFilterUi === 'expired') return { enabled: true, isExpired: true }
    return undefined
  }, [listFilterUi])

  const filterWithSearch = useMemo((): CouponFilterInput | undefined => {
    const query = searchQuery.trim()
    if (!query) return filterArg
    return { ...(filterArg ?? {}), search: query }
  }, [filterArg, searchQuery])

  const orderDir =
    sortDirection === 'ASC' ? OrderDirection.Asc : OrderDirection.Desc

  const { data, loading, error, refetch } = useCouponsQuery({
    variables: {
      filter: filterWithSearch,
      sortBy: sortField,
      sortDirection: orderDir,
    },
    skip: false,
    fetchPolicy: 'cache-and-network',
  })

  const [updateCoupon] = useUpdateCouponMutation()
  const [deleteCoupon] = useDeleteCouponMutation()

  useFocusEffect(
    React.useCallback(() => {
      refetch()
    }, [refetch])
  )

  useEffect(() => {
    if (error) {
      console.error(error)
      Sentry.captureException(error)
    }
  }, [error])

  const coupons = data?.coupons ?? []

  const handleDelete = async (coupon: AdminCouponRow) => {
    setBusyId(coupon.id)
    try {
      await deleteCoupon({ variables: { id: coupon.id } })
      Toast.show(t('delete-coupon-success'), MessageType.info)
      await refetch()
    } catch (e: unknown) {
      const msg = getFirstGraphQlErrorMessage(e)?.toLowerCase() ?? ''
      if (msg.includes('redeemed') || msg.includes('conflict')) {
        Toast.show(t('delete-coupon-conflict'), MessageType.error)
      } else {
        Toast.show(t('delete-coupon-error'), MessageType.error)
      }
      Sentry.captureException(e)
    } finally {
      setBusyId(null)
    }
  }

  const handleToggle = async (coupon: AdminCouponRow) => {
    setBusyId(coupon.id)
    try {
      await updateCoupon({
        variables: {
          input: { id: coupon.id, enabled: !coupon.enabled },
        },
      })
      await refetch()
    } catch (e) {
      Toast.show(t('toggle-error'), MessageType.error)
      Sentry.captureException(e)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <ScreenWrapper style={{ padding: 10 }} withScrollView={false}>
      <Column gap={4} flex={1}>
        <Row columnGap={4} wrap alignItems="center">
          <IconButton
            mode="outlined"
            icon="arrow-left"
            onPress={() => navigation.goBack()}
          />
          <View style={{ flex: 1, minWidth: 0 }}>
            <MelddSearchBar
              placeholder={t('search-placeholder')}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>
          <IconButton
            mode="contained-tonal"
            icon="plus"
            accessibilityLabel={t('add-coupon-a11y')}
            onPress={() => {
              setModalCoupon(null)
              setCouponModalVisible(true)
            }}
          />
        </Row>
        <Column gap={2}>
          <Text variant="labelMedium">{t('filter-status')}</Text>
          <Row alignItems="center" columnGap={8} wrap>
            <View style={{ flex: 1, minWidth: 150 }}>
              <Dropdown
                items={FILTER_DROPDOWN_ORDER.map((id) => ({
                  id,
                  label: t(FILTER_LABEL_KEYS[id]),
                }))}
                selected={t(FILTER_LABEL_KEYS[listFilterUi])}
                showSearch={false}
                onSelect={(item) => {
                  setListFilterUi(item.id as ListFilterUi)
                }}
              />
            </View>
            <View style={{ justifyContent: 'center', alignItems: 'center' }}>
              <SortOptions<CouponSortField>
                options={[
                  {
                    field: CouponSortField.CreatedAt,
                    direction: 'DESC',
                    label: t('sort-created-desc'),
                    icon: 'sort-calendar-descending',
                  },
                  {
                    field: CouponSortField.CreatedAt,
                    direction: 'ASC',
                    label: t('sort-created-asc'),
                    icon: 'sort-calendar-ascending',
                  },
                  {
                    field: CouponSortField.Code,
                    direction: 'ASC',
                    label: t('sort-code-asc'),
                    icon: 'sort-alphabetical-ascending',
                  },
                  {
                    field: CouponSortField.Code,
                    direction: 'DESC',
                    label: t('sort-code-desc'),
                    icon: 'sort-alphabetical-descending',
                  },
                  {
                    field: CouponSortField.ValidTo,
                    direction: 'DESC',
                    label: t('sort-expiry-desc'),
                    icon: 'sort-calendar-descending',
                  },
                  {
                    field: CouponSortField.ValidTo,
                    direction: 'ASC',
                    label: t('sort-expiry-asc'),
                    icon: 'sort-calendar-ascending',
                  },
                  {
                    field: CouponSortField.RedeemCount,
                    direction: 'DESC',
                    label: t('sort-usage-desc'),
                    icon: 'sort-numeric-descending',
                  },
                  {
                    field: CouponSortField.RedeemCount,
                    direction: 'ASC',
                    label: t('sort-usage-asc'),
                    icon: 'sort-numeric-ascending',
                  },
                ]}
                selectedField={sortField}
                selectedDirection={sortDirection}
                onSortChange={(field, direction) => {
                  setSortField(field)
                  setSortDirection(direction)
                }}
              />
            </View>
          </Row>
        </Column>

        {error ? <Text variant="bodyMedium">{t('load-error')}</Text> : null}

        {loading && !data ? (
          <ActivityIndicator style={{ marginTop: 24 }} />
        ) : (
          <FlatList
            data={coupons}
            keyExtractor={(item) => item.id}
            refreshControl={
              <RefreshControl
                refreshing={loading}
                onRefresh={() => refetch()}
              />
            }
            ListEmptyComponent={
              <Text variant="bodyMedium" style={{ marginTop: 16 }}>
                {t('empty')}
              </Text>
            }
            renderItem={({ item }) => (
              <CouponCard
                coupon={item}
                onEdit={(c) => {
                  setModalCoupon(c)
                  setCouponModalVisible(true)
                }}
                onToggleEnabled={handleToggle}
                onDelete={handleDelete}
                onShowRedemptions={(coupon) => {
                  setRedemptionsCoupon(coupon)
                  setRedemptionsVisible(true)
                }}
                busy={busyId === item.id}
              />
            )}
            contentContainerStyle={{ flexGrow: 1, paddingBottom: 24 }}
          />
        )}
      </Column>

      <EditCouponModal
        visible={couponModalVisible}
        coupon={modalCoupon}
        onDismiss={() => {
          setCouponModalVisible(false)
          setModalCoupon(null)
        }}
        onSaved={() => refetch()}
      />

      <CouponRedemptionsModal
        visible={redemptionsVisible}
        coupon={redemptionsCoupon}
        onDismiss={() => {
          setRedemptionsVisible(false)
          setRedemptionsCoupon(null)
        }}
      />
    </ScreenWrapper>
  )
}
